/* eslint-disable no-console */
import {
    Button,
    CircularProgress,
    IconButton,
    Snackbar,
    TextField,
    MenuItem,
    Select,
    InputLabel,
    FormControl
} from "@mui/material";
import { Close, ArrowBack } from "@mui/icons-material";
import {
	default as React,
	useEffect,
	useReducer,
	useState
} from "react";
import {
	useHistory,
	/*useLocation,*/ useParams
} from "react-router-dom";
import styled from "styled-components";
import { /* useBarUtils, */ useViewedBar, asyncGetBarImages } from "../actions/bars";
import { useSetHeaderTitle } from "../actions/headerContext";
import {
    usePromotionUtils,
    useSelectedPromotion
} from "../actions/promotions";
// import { usePromotionPlans } from "../actions/promotionPlans"; // Payment flow temporarily disabled
import { auth, firebase } from "../services/firebase";
import { THEME_MAP } from "../theme";
import { BackDrop } from "../components/BackDrop";
import { useUserRoles } from "../globalstore";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { captureError } from "../lib/errorReporting";
dayjs.extend(isBetween);

const PROMOTION_DURATION_OPTIONS = [
    { value: "1", label: "1 Hour" },
    { value: "2", label: "2 Hours" },
    { value: "3", label: "3 Hours" },
    { value: "4", label: "4 Hours" },
    { value: "5", label: "5 Hours" },
    { value: "6", label: "6 Hours" },
    { value: "9", label: "9 Hours" },
    { value: "12", label: "12 Hours" },
    { value: "24", label: "24 Hours" },
    { value: "continual", label: "Continual" }
];

const CONTINUAL_DURATION_FALLBACK_DAYS = 365; // Ensures continual promos stay active without end-date gaps

export function PromotionDetail() {
	const { id } = useParams();
	const history = useHistory();
	const promotionSnapshot = useSelectedPromotion();
	const { fetchPromotionById } = usePromotionUtils();

	const [promotion, setPromotion] = useState();
	const [isFetching, setIsFetching] = useState();

	const [isOpen, setIsOpen] = useState(false);
	const [note, setNote] = useState("");

	const setHeaderTitle = useSetHeaderTitle();
	const closeNote = (_event, reason) => {
		if (reason === "clickaway") {
			return;
		}
		setNote("");
		setIsOpen(false);
	};

	const openNote = (message) => {
		setNote(message);
		setIsOpen(true);
	};

	useEffect(() => {
		setHeaderTitle(id === "new" && "Create new promotion");
		if (promotionSnapshot && promotionSnapshot.id === id) {
			let _promotion = promotionSnapshot;
			delete _promotion.bar;
			setPromotion(_promotion);
		} else if (id) {
			setPromotion(null);
			if (id !== "new") {
				setIsFetching(true);
				fetchPromotionById(id).then(() => setIsFetching(false)).catch((err) => {
					captureError(err, { view: "PromotionDetail", action: "fetchPromotionById" });
					console.error("Failed to fetch promotion", err);
					setIsFetching(false);
				});
			}
		}
	}, [promotionSnapshot, id, fetchPromotionById, setHeaderTitle]);

	if (isFetching)
		return (
			<Container className={"is-loading"}>
				<LoadingFog className={"loading-fog is-loading"}>
					<CircularProgress color="primary" />
				</LoadingFog>
			</Container>
		);

	return (
		<Container>
			<BackButton onClick={() => history.goBack()}>
				<ArrowBack fontSize="small" />
				Back
			</BackButton>
			<PromotionInfoSection
				promotion={promotion}
				openNote={openNote}
			/>
			<Snackbar
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right"
				}}
				open={isOpen}
				autoHideDuration={3000}
				onClose={closeNote}
				message={note}
				action={
					<IconButton
						size="small"
						aria-label="close"
						color="inherit"
						onClick={closeNote}
					>
						<Close fontSize="small" />
					</IconButton>
				}
			/>
		</Container>
	);
}
const Container = styled.div`
	height: 100%;
	width: 100%;

	padding: 24px;
	overflow: auto;
	position: relative;

	&.is-loading {
		overflow: hidden;
	}

	.promotion-actions {
		max-width: 900px;
		margin: auto;
		display: flex;
		justify-content: flex-end;
	}

	@media (max-width: 767px) {
		padding: 16px;
	}
`;

const BackButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	background: none;
	border: none;
	cursor: pointer;
	color: ${THEME_MAP["color-primary-500"]};
	font-size: 0.9rem;
	padding: 4px 0;
	margin-bottom: 12px;

	&:hover {
		text-decoration: underline;
	}
`;

const LoadingFog = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: none;
	align-items: center;
	justify-content: center;
	background: rgba(255, 235, 235, 0.6);
	&.is-loading {
		display: flex;
	}
`;

// Creating a new promotion from promotion/new
function PromotionInfoSection({ promotion, openNote }) {
    const history = useHistory();
    const { currentUser } = auth;
    const { userRoles: { isAdmin } } = useUserRoles();
    const { id } = useParams();
    const isNew = id === "new";
    const selectedBar = useViewedBar();
    const [showBackDrop, setShowBackdrop] = useState(false);
    const { savePromotion } = usePromotionUtils();
    const [promotionState, promotionDispatch] = useReducer(
        promotionInfoReducer,
        emptyPromotionState,
        promotionInfoInit
    );
    const [startDateTime, setStartDateTime] = useState(() =>
        dayjs()
            .second(0)
            .millisecond(0)
            .format("YYYY-MM-DDTHH:mm")
    );
    const [selectedDuration, setSelectedDuration] = useState("");
    const [computedEndDateTime, setComputedEndDateTime] = useState("");
    const [barImageUrl, setBarImageUrl] = useState(null);

    // Load the selected bar's cover image so the preview mirrors how the
    // promotion will appear in the app (cards show the bar image, like BarCard).
    useEffect(() => {
        let cancelled = false;
        const bar = selectedBar;
        if (!bar?.id || !Array.isArray(bar.barImages) || bar.barImages.length === 0) {
            setBarImageUrl(null);
            return undefined;
        }
        asyncGetBarImages(bar.id, bar)
            .then((urls) => {
                if (cancelled || !Array.isArray(urls) || urls.length === 0) return;
                const coverIndex = bar.barCoverImage
                    ? bar.barImages.findIndex((name) => name === bar.barCoverImage)
                    : -1;
                setBarImageUrl((coverIndex >= 0 ? urls[coverIndex] : urls[0]) || null);
            })
            .catch((err) => {
                captureError(err, { view: "PromotionDetail", action: "loadBarPreviewImage" });
            });
        return () => {
            cancelled = true;
        };
    }, [selectedBar]);

    useEffect(() => {
        let promo = promotion;
        if (promotion && !promotion["barId"]) {
            promo = promotion.data();
        }
        promotionDispatch({
            type: "reset",
            payload: promo || emptyPromotionState
        });
    }, [promotion]);

    useEffect(() => {
        const promoData = promotion?.barId
            ? promotion
            : promotion?.data?.()
                ? promotion.data()
                : promotion;

        if (!promoData || !promoData.promotionStartingHours) {
            const defaultStart = dayjs()
                .second(0)
                .millisecond(0)
                .format("YYYY-MM-DDTHH:mm");
            setStartDateTime(defaultStart);
            const defaultEnd = selectedDuration
                ? dayjs(defaultStart)
                    .add(Number(selectedDuration), "hour")
                    .format("YYYY-MM-DDTHH:mm")
                : "";
            setComputedEndDateTime(defaultEnd);
            return;
        }

    const startingMoment = dayjs(
        promoData.promotionStartingHours.toDate()
    );
    setStartDateTime(
        startingMoment
            .second(0)
            .millisecond(0)
            .format("YYYY-MM-DDTHH:mm")
    );

    let detectedDuration = promoData.promotionDurationHours;
    let endingMoment = promoData.promotionEndingHours
        ? dayjs(promoData.promotionEndingHours.toDate())
        : null;

    if (promoData.promotionIsContinual) {
        setSelectedDuration("continual");
        if (!endingMoment) {
            endingMoment = startingMoment.add(
                CONTINUAL_DURATION_FALLBACK_DAYS,
                "day"
            );
        }
    } else if (detectedDuration != null) {
        setSelectedDuration(String(detectedDuration));
        endingMoment = startingMoment.add(detectedDuration, "hour");
    } else if (endingMoment) {
        const diffHours = Math.max(
            endingMoment.diff(startingMoment, "hour"),
            1
        );
        if (diffHours > 24) {
            setSelectedDuration("continual");
        } else {
            setSelectedDuration(String(diffHours));
        }
    } else {
        setSelectedDuration("1");
        endingMoment = startingMoment.add(1, "hour");
    }

    if (!endingMoment) {
        endingMoment = startingMoment.add(1, "hour");
    }

    setComputedEndDateTime(
        endingMoment
            .second(0)
            .millisecond(0)
            .format("YYYY-MM-DDTHH:mm")
    );
    // Note: promotionDays is derived again when saving; existing values are preserved by savePromotion for updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promotion]);

    useEffect(() => {
        if (!startDateTime || !selectedDuration) {
            return;
        }
        const startMoment = dayjs(startDateTime);
        if (!startMoment.isValid()) {
            return;
        }
        if (selectedDuration === "continual") {
            setComputedEndDateTime(
                startMoment
                    .add(CONTINUAL_DURATION_FALLBACK_DAYS, "day")
                    .second(0)
                    .millisecond(0)
                    .format("YYYY-MM-DDTHH:mm")
            );
            return;
        }
        const parsedHours = parseInt(selectedDuration, 10);
        if (Number.isNaN(parsedHours)) {
            return;
        }
        setComputedEndDateTime(
            startMoment
                .add(parsedHours, "hour")
                .second(0)
                .millisecond(0)
                .format("YYYY-MM-DDTHH:mm")
        );
    }, [startDateTime, selectedDuration]);

    const handleInputChange = (event) => {
        promotionDispatch({
            type: "update",
            payload: {
                name: event.target.name,
                value: event.target.value
            }
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!validateData()) {
            openNote("Promotion must include a name, start time, and duration.");
            return;
        }

        if (isNew && !selectedBar?.id) {
            openNote("A bar must be selected before creating a promotion.");
            return;
        }

        const startMoment = dayjs(startDateTime);
        if (!startMoment.isValid()) {
            openNote("Start time is invalid. Please pick a valid date and time.");
            return;
        }

        const isContinual = selectedDuration === "continual";
        const parsedHours = isContinual
            ? null
            : parseInt(selectedDuration, 10);

        if (!isContinual && (Number.isNaN(parsedHours) || parsedHours <= 0)) {
            openNote("Duration must be one of the available hour options.");
            return;
        }

        setShowBackdrop(true);

        try {
            const endMoment = isContinual
                ? startMoment.add(CONTINUAL_DURATION_FALLBACK_DAYS, "day")
                : startMoment.add(parsedHours, "hour");

            const payload = {
                promotionName: promotionState.promotionName.trim(),
                promotionDescription: promotionState.promotionDescription?.trim() || "",
                promotionDurationHours: isContinual ? null : parsedHours,
                promotionIsContinual: isContinual,
                promotionStartingHours: firebase.firestore.Timestamp.fromDate(
                    startMoment.toDate()
                ),
                promotionEndingHours: firebase.firestore.Timestamp.fromDate(
                    endMoment.toDate()
                ),
                promotionDays: [startMoment.format("YYYY-MM-DD")],
                status: true,
                updatedAt: firebase.firestore.Timestamp.now()
            };

            if (isNew) {
                payload.createdBy = currentUser.uid;
                payload.barId = selectedBar.id;
                payload.userId = currentUser.uid;
                payload.createdAt = firebase.firestore.Timestamp.now();
            }

            const promotionId = await savePromotion(id, payload);

            setShowBackdrop(false);
            openNote("Promotion saved and activated.");
            
            if (isNew) {
                // Redirect to promotions list after creating new promotion
                if (isAdmin === "true" || isAdmin === true) {
                    // Admin users go to the admin promotions page
                    history.push("/promotions");
                } else if (selectedBar?.id) {
                    // Non-admin users go to their bar's promotions page
                    history.push(`/promotions/${selectedBar.id}`);
                } else {
                    // Fallback to general promotions page
                    history.push("/promotions");
                }
            } else {
                // Stay on detail page when editing existing promotion
                history.push(`/promotion/${promotionId}`);
            }
        } catch (error) {
            captureError(error, { view: "PromotionDetail", action: "savePromotion" });
            console.error("Error while saving promotion:", error);
            setShowBackdrop(false);
            openNote(`An error occurred: ${error.message}`);
        }

        /*
         * Previous payment + trial activation workflow retained for future reference while promotions are free.
         *
         * if (isNew) {
         *   if (trialStatus === "eligible") { ... }
         *   else if (trialStatus === "active") { ... }
         *   else if (trialStatus === "used" || trialStatus === "error" || trialStatus === "not_applicable") { ... }
         * }
         */
    };


    const handleCancel = (e) => {
        e.preventDefault();
        history.goBack();
    };

    const validateData = () => {
        return Boolean(
            promotionState.promotionName?.trim() &&
            startDateTime &&
            selectedDuration
        );
    };

    // --- Determine Button Text and Price Display ---
    const activateButtonText = isNew ? "Save & Activate" : "Save";

    const previewBarName =
        selectedBar?.barName || selectedBar?.bar_name || selectedBar?.name || "Your Bar";

	return (
        <>
        <PromotionInfoContainer onSubmit={handleSave}> {/* Use onSubmit for form */}
				<TextField
					className="header-like"
					label="Promotion Name"
					name="promotionName"
					fullWidth
					inputProps={{
						style: {
							fontSize: "1.8rem"
						},
						maxLength: 50
					}}
					value={promotionState.promotionName}
					onChange={handleInputChange}
					required
					disabled={!isNew && !promotionState.hasChanged} // Allow editing existing name if changed
					data-tour="promo-name"
				/>
				<TextField
					label="Promotion Description"
					name="promotionDescription"
					fullWidth
					multiline
					value={promotionState.promotionDescription}
					onChange={handleInputChange}
					data-tour="promo-description"
				/>
                <TextField
                    label="Start Date & Time"
                    type="datetime-local"
                    value={startDateTime}
                    onChange={(event) => setStartDateTime(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                    disabled={!isNew}
                    data-tour="promo-timing"
                />
           <FormControl fullWidth required>
                <InputLabel id="duration-select-label" shrink>
                    Duration *
                </InputLabel>
                <Select
                    displayEmpty
                    labelId="duration-select-label"
                    label="Duration *"
                    value={selectedDuration}
                    onChange={(event) => setSelectedDuration(event.target.value)}
                    disabled={!isNew}
                >
                    {selectedDuration ? null : (
                        <MenuItem value="">
                            <em>-- Select Duration --</em>
                        </MenuItem>
                    )}
                    {PROMOTION_DURATION_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
                <TextField
                    label={selectedDuration === "continual" ? "Review Date" : "Ends"}
                    type="datetime-local"
                    value={computedEndDateTime}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    disabled
                    helperText={
                        selectedDuration === "continual"
                            ? "Continual promotions stay active until you disable them."
                            : "End time is calculated from the selected duration."
                    }
                />

            <div className="form-footer">
                {isNew ? (
                    <Button
                        color="primary"
                        variant={"outlined"}
                        onClick={handleCancel}
                        disabled={showBackDrop} // Disable cancel when loading
                    >
                        Cancel
                    </Button>
                ) : null}
                <Button
                    type="submit"
                    color="primary"
                    variant={
                        promotionState.hasChanged || isNew
                            ? "contained"
                            : "outlined"
                    }
                    disabled={
                        showBackDrop ||
                        (isNew && (!startDateTime || !selectedDuration)) ||
                        (!isNew && !promotionState.hasChanged)
                    }
                    data-tour="promo-save"
                >
                    {activateButtonText}
                </Button>
            </div>
            {showBackDrop ? (
                <BackDrop showBackDrop={showBackDrop} />
            ) : null}
        </PromotionInfoContainer>
        <PreviewSection>
            <PreviewHeading>App Preview</PreviewHeading>
            <PreviewCard data-tour="promo-preview">
                {barImageUrl ? (
                    <div className="image-container">
                        <img className="bg" src={barImageUrl} alt="" aria-hidden="true" />
                        <img className="fg" src={barImageUrl} alt={`${previewBarName} cover`} />
                    </div>
                ) : (
                    <div className="image-container placeholder">
                        <span>No bar image yet</span>
                    </div>
                )}
                <div className="info">
                    <span className="bar-name">{previewBarName}</span>
                    {promotionState.promotionName ? (
                        <span className="promo-name">{promotionState.promotionName}</span>
                    ) : (
                        <span className="promo-name is-placeholder">Promotion name</span>
                    )}
                    {promotionState.promotionDescription ? (
                        <span className="promo-description">
                            {promotionState.promotionDescription}
                        </span>
                    ) : null}
                </div>
            </PreviewCard>
        </PreviewSection>
        </>
    );
}
const emptyPromotionState = {
	promotionName: "",

	promotionCoverImage: "",
	promotionImages: [],
	promotionDescription: "",

	promotionDays: "",
	promotionEndingHours: "",
	promotionStartingHours: "",
	promotionPlanId: "",
    promotionDurationHours: null,
    promotionIsContinual: false,
	hasChanged: false,
	status: false

	//position: {
	//geopoint: {
	//latitude: 0,
	//longitude: 0,
	//},
	//},
};

function promotionInfoInit(promotionInfo) {
	return {
		original: promotionInfo,
		...promotionInfo,
		hasChanged: false
	};
}

function promotionInfoReducer(state, action) {
	const { type, payload } = action;
	let hasChanged = false;
	switch (type) {
		case "reset":
			return promotionInfoInit(payload || state.original);
		case "update":
			hasChanged = true;
			return {
				...state,
				[payload.name]: payload.value,
				hasChanged
			};
		default:
			return state;
	}
}

const PromotionInfoContainer = styled.form`
	width: 100%;
	margin: auto;
	background: ${THEME_MAP["color-basic-100"]};
	border-radius: 4px;
	border: 1px solid ${THEME_MAP["color-basic-400"]};
	padding: 16px 24px;
	gap: 16px; // Use gap for spacing between flex items

	min-height: 100px;
	margin-bottom: 16px;

	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;

    & > .MuiTextField-root,
    & > .MuiFormControl-root {
        width: 100%; // Make form elements take full width within the flex container
        // Margins are handled by 'gap', no need for margin-top/bottom here
    }

	.image-uploads {
		background: ${THEME_MAP["color-basic-300"]};
		border-radius: 3px;
		height: 300px;

		display: flex;
		align-items: center;
		justify-content: space-evenly;

		img {
			height: 268px;
			width: auto;
		}
	}

	.form-footer {
		width: 100%;
		display: flex;
		justify-content: flex-end;

		& > button {
			margin-left: 16px;
		}
	}
`;

const PreviewSection = styled.div`
	width: 100%;
	margin: 0 auto 24px;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const PreviewHeading = styled.h3`
	width: 100%;
	margin: 0 0 12px;
	font-size: 0.95rem;
	font-weight: 600;
	color: ${THEME_MAP["color-basic-700"]};
	text-align: center;
`;

const PreviewCard = styled.div`
	width: 340px;
	max-width: 100%;
	background: ${THEME_MAP["color-basic-100"]};
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	border-bottom: 1px solid ${THEME_MAP["color-basic-400"]};
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	overflow: hidden;

	.image-container {
		position: relative;
		width: 100%;
		height: 190px;
		overflow: hidden;
		background: ${THEME_MAP["color-basic-300"]};
		border-top-left-radius: 8px;
		border-top-right-radius: 8px;
	}

	.image-container img.bg {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		filter: blur(30px);
		transform: scale(1.1);
	}

	.image-container img.fg {
		position: relative;
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	.image-container.placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		color: ${THEME_MAP["color-basic-600"]};
		font-size: 0.85rem;
	}

	.info {
		padding: 12px;
		display: flex;
		flex-direction: column;
	}

	.bar-name {
		font-size: 14px;
		font-weight: 700;
		color: ${THEME_MAP["color-basic-900"]};
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.promo-name {
		font-size: 24px;
		font-weight: 700;
		color: ${THEME_MAP["color-primary-500"]};
		margin-top: 2px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.promo-name.is-placeholder {
		color: ${THEME_MAP["color-basic-500"]};
	}

	.promo-description {
		font-size: 13px;
		color: ${THEME_MAP["color-basic-700"]};
		line-height: 16px;
		margin-top: 4px;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}
`;

// const TrialInfoText = styled.p`
// 	width: 100%;
// 	margin-top: 16px;
// 	margin-bottom: 16px;
// 	color: ${THEME_MAP["color-basic-700"]};
// 	font-style: italic;
// 	text-align: center; // Or left align as needed
// `;

// const emptyCategories = {
// 	monday: {},
// 	tuesday: {},
// 	wednesday: {},
// 	thursday: {},
// 	friday: {},
// 	saturday: {},
// 	sunday: {}
// };
// const DAYS = [
// 	"monday",
// 	"tuesday",
// 	"wednesday",
// 	"thursday",
// 	"friday",
// 	"saturday",
// 	"sunday"
// ];

// const emptyPromotions = {
// 	monday: {},
// 	tuesday: {},
// 	wednesday: {},
// 	thursday: {},
// 	friday: {},
// 	saturday: {},
// 	sunday: {}
// };

// const resizeExample = () => {
//   let imageRef = { files: [new File()] };
//   resizeImage({
//     file: imageRef.files[0],
//     maxSize: 500,
//   })
//     .then(function (resizedImage) {
//       // upload resized image
//     })
//     .catch(function (err) {
//       console.error(err);
//     });
// };
